import { onlineManager } from '@tanstack/react-query';

export type ConnectivityState = 'offline' | 'online' | 'reconnecting';

export type ConnectivitySignal = {
  getInitialStatus: () => Promise<boolean>;
  subscribe: (listener: (connected: boolean) => void) => (() => void) | Promise<() => void>;
};

export type BrowserConnectivityEnvironment = Pick<
  Window,
  'addEventListener' | 'removeEventListener'
> & {
  navigator: Pick<Navigator, 'onLine'>;
};

export type NativeNetworkPlugin = {
  addListener: (
    eventName: 'networkStatusChange',
    listener: (status: { connected: boolean }) => void,
  ) => Promise<{ remove: () => void }>;
  getStatus: () => Promise<{ connected: boolean }>;
};

type ConnectivityControllerOptions = {
  signal: ConnectivitySignal;
  setOnline?: (online: boolean) => void;
};

type ConnectivityListener = (state: ConnectivityState) => void;

export type ConnectivityController = {
  getState: () => ConnectivityState;
  recordRequestFailure: () => void;
  recordRequestSuccess: () => void;
  start: () => Promise<void>;
  stop: () => void;
  subscribe: (listener: ConnectivityListener) => () => void;
};

export const createConnectivityController = ({
  signal,
  setOnline = (online) => onlineManager.setOnline(online),
}: ConnectivityControllerOptions): ConnectivityController => {
  let state: ConnectivityState = 'online';
  let removeSignalListener: (() => void) | undefined;
  const listeners = new Set<ConnectivityListener>();

  const setState = (nextState: ConnectivityState, force = false) => {
    if (!force && state === nextState) return;

    state = nextState;
    setOnline(nextState !== 'offline');
    listeners.forEach((listener) => listener(nextState));
  };

  return {
    getState: () => state,
    recordRequestFailure: () => setState('offline'),
    recordRequestSuccess: () => setState('online'),
    async start() {
      const connected = await signal.getInitialStatus();
      setState(connected ? 'online' : 'offline', true);
      removeSignalListener = await signal.subscribe((nextConnected) => {
        setState(nextConnected ? 'reconnecting' : 'offline');
      });
    },
    stop() {
      removeSignalListener?.();
      removeSignalListener = undefined;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

export const createBrowserConnectivitySignal = (
  browser: BrowserConnectivityEnvironment = window,
): ConnectivitySignal => ({
  getInitialStatus: async () => browser.navigator.onLine,
  subscribe(listener) {
    const onOnline = () => listener(true);
    const onOffline = () => listener(false);

    browser.addEventListener('online', onOnline);
    browser.addEventListener('offline', onOffline);

    return () => {
      browser.removeEventListener('online', onOnline);
      browser.removeEventListener('offline', onOffline);
    };
  },
});

export const createNativeConnectivitySignal = (
  network: NativeNetworkPlugin,
): ConnectivitySignal => ({
  async getInitialStatus() {
    return (await network.getStatus()).connected;
  },
  async subscribe(listener) {
    const handle = await network.addListener('networkStatusChange', (status) => {
      listener(status.connected);
    });
    return () => handle.remove();
  },
});
